
import { Component, Vue, Prop, Watch, Inject } from "vue-property-decorator"

import { get, set } from "lodash"

// @ts-ignore
import DatePicker from "v-calendar/lib/components/date-picker.umd"
import vSelect from "vue-select"
import HelpIcon from "@/components/New/Page/PageComponents/HelpIcon.vue"
import DateTimezoneInfo from "@/components/Fields/DateTimezoneInfo.vue"

import moment from "moment"
import { Getter, State } from "vuex-class"
import { TimeZone } from "@/types"
import { DateTime } from "luxon"
import { asUtc } from "@/scripts/defaultTimezone"
import { BButton } from "bootstrap-vue"

@Component({
	components: {
		"help-icon": HelpIcon,
		"v-select": vSelect,
		"v-date-picker": DatePicker,
		"timezone-info": DateTimezoneInfo
	}
})
export default class DateTimePicker extends Vue {
	$refs!: {
		minute: HTMLInputElement,
		hour: HTMLInputElement,
		day: HTMLInputElement[],
		month: HTMLInputElement[],
		year: HTMLInputElement[],
		datePickerButton: BButton,
		yesterdayButton: BButton,
		todayButton: BButton,
		tomorrowButton: BButton,
		datePicker: any
	}

	@Prop()
	value!: string | Date | null;

	@Prop()
	includeTime!: boolean;

	@Prop({ type: Boolean })
	endOfDay!: boolean

	@Prop({ default: true })
	includeYesterday!: boolean;

	@Prop({ default: true })
	includeToday!: boolean;

	@Prop({ type: Boolean, default: true })
	includeTomorrow!: boolean;

	@Prop({ default: false })
	readonly!: boolean;

	@Prop({ default: true })
	convertDatesToUTC!: boolean;

	@Prop({ type: Boolean })
	enterAsUTC!: boolean;

	@Prop({ default: null })
	minDateTime!: Date | string | null;

	@Prop({ default: null })
	maxDateTime!: Date | string | null;

	@Prop({ type: Boolean })
	raiseStringValue!: boolean

	@Prop({ type: Boolean })
	small!: boolean

	@Prop({ type: Boolean })
	noClear!: boolean

	@Inject({ default: false })
	noPopup!: boolean;

	@State displayTimezone!: TimeZone | null
	@Getter("getTenantTimezones") tenantTimezones!: TimeZone[]
	@Getter("getUseV2") getUseV2!: boolean

	public dateValueParsed: Date | null = null;
	public dateValueISO: string | null = null;

	public dateParts: any[] = [];

	ampm: any = null;
	ampmSelectOptions = [{ label: "AM", value: "am" }, { label: "PM", value: "pm" }];

	year: string = "";
	month: string = "";
	day: string = "";

	hour: string = "";
	minute: string = "";

	dateIsInvalid: boolean = false;
	timeIsInvalid: boolean = false;

	displayDate: DateTime | null = null
	pickingDate: boolean = false

	mounted () {
		this.dateParts = [
			{
				key: "day",
				label: "Day",
				formatPart: "d",
				order: 1,
				styling: this.getUseV2 && !this.small ? "min-width: 2.5rem" : "min-width: 50px"
			},
			{
				key: "month",
				label: "Month",
				formatPart: "m",
				order: 2,
				styling: this.getUseV2 && !this.small ? "min-width: 2.5rem" : "min-width: 50px"	// Temp fix for v2, will be replaced with a new date picker later
			},
			{
				key: "year",
				label: "Year",
				formatPart: "y",
				order: 3,
				styling: this.getUseV2 && !this.small ? "min-width: 5rem" : "min-width: 50px"
			}
		]

		this.setValue(this.value)

		// Sort dateparts based on locale
		const dateLocale = (moment.localeData() as any)._longDateFormat.L.toLowerCase()

		this.dateParts.forEach((datePart:any) => {
			datePart.order = dateLocale.indexOf(datePart.formatPart)
		})
		this.dateParts.sortBy("order")
	}

	@Watch("value")
	valueChanged (value: string | Date | null) {
		this.setValue(value)
	}

	@Watch("displayTimezone")
	onDisplayTimezoneChange (timezone: TimeZone | null) {
		if (this.displayDate && !this.enterAsUTC) {
			this.setDisplayDate(this.displayDate.setZone(timezone ? timezone.iana : "system"))
		}
	}

	/**
	 * If days, months or years have been entered
	 */
	public get anyDateValue (): boolean {
		return !!this.day ||
			!!this.month ||
			!!this.year
	}

	/**
	 * If hours or minutes have been entered
	 */
	public get anyTimeValue (): boolean {
		return !!this.hour ||
			!!this.minute
	}

	/**
	 * Updates text boxes based on the value model change.
	 *
	 * @param value - A Date object or an ISO date string.
	 * @param normalizeTime - If true and includeTime is false, normalizes the time component:
	 *                        - Sets to 00:00:00.000 if endOfDay is false.
	 *                        - Sets to 23:59:59.000 if endOfDay is true.
	 *                        Ensures consistent time values when only the date is relevant.
	 */
	private setValue (value: string | Date | null, normalizeTime: boolean = false) {
		if (value) {
			const readOptions = this.enterAsUTC
				? { zone: "utc" }
				: {}
			let dateTime = value instanceof Date
				? DateTime.fromJSDate(value, readOptions)
				: DateTime.fromISO(value, { setZone: true, ...readOptions })

			if (!(value instanceof Date) && !this.enterAsUTC) {
				// Dates reads from ISO strings do not properly convert to local time.
				dateTime = dateTime.setZone(this.displayTimezone
					? this.displayTimezone.iana
					: Intl.DateTimeFormat().resolvedOptions().timeZone)
			}

			if (!this.includeTime && normalizeTime) {
				if (this.endOfDay) {
					dateTime = dateTime.set({
						hour: 23,
						minute: 59,
						second: 59,
						millisecond: 0
					})
				} else {
					dateTime = dateTime.set({
						hour: 0,
						minute: 0,
						second: 0,
						millisecond: 0
					})
				}
			}
			this.setDisplayDate(dateTime)

			if (!dateTime.isValid) {
				console.error("Invalid date", value, dateTime.invalidReason)
				return
			}

			this.raiseValue(dateTime)
		} else {
			this.year = ""
			this.month = ""
			this.day = ""
			this.hour = ""
			this.minute = ""
			this.ampm = ""

			this.displayDate = null
		}
	}

	/**
	 * Update value using text box values
	 */
	updateDate () {
		const readOptions = this.enterAsUTC
			? { zone: "utc" }
			: {}

		const hourMinuteSecond: { hour: number, minute: number, second: number } = { hour: 0, minute: 0, second: 0 }
		if (this.includeTime) {
			hourMinuteSecond.hour = this.includeTime ? parseInt(this.hour) : 0
			if (this.ampm === "pm" && hourMinuteSecond.hour < 12) {
				hourMinuteSecond.hour += 12
			} else if (this.ampm === "am" && hourMinuteSecond.hour >= 12) {
				hourMinuteSecond.hour -= 12
			}
			hourMinuteSecond.minute = parseInt(this.minute)
		} else if (this.endOfDay) {
			hourMinuteSecond.hour = 23
			hourMinuteSecond.minute = 59
			hourMinuteSecond.second = 59
		}

		const dateTime = (DateTime as any).fromObject({
			year: parseInt(this.year),
			month: parseInt(this.month),
			day: parseInt(this.day),
			hour: hourMinuteSecond.hour,
			minute: hourMinuteSecond.minute,
			second: hourMinuteSecond.second
		}, readOptions) as DateTime

		if (dateTime.isValid) {
			this.timeIsInvalid = false
			this.dateIsInvalid = false

			this.raiseValue(dateTime)
		} else {
			const dateWithoutTime = (DateTime as any).fromObject({
				year: parseInt(this.year),
				month: parseInt(this.month),
				day: parseInt(this.day),
				hour: 0,
				minute: 0,
				second: 0
			}, readOptions) as DateTime

			if (dateWithoutTime.isValid) {
				this.timeIsInvalid = true
			} else {
				this.dateIsInvalid = true
			}
		}
	}

	raiseValue (dateValue: DateTime) {
		const newDate = dateValue.toUTC().toJSDate()

		if (this.value instanceof Date) {
			if (this.dateAsString(this.value) === this.dateAsString(newDate)) {
				return
			}
		} else if (typeof this.value === "string") {
			if (new Date(this.value).getTime() === new Date(newDate.toISOString()).getTime()) {
				return
			}
		}

		if (this.raiseStringValue) {
			this.$emit("input", dateValue.toJSDate().toISOString())
		} else {
			this.$emit("input", dateValue.toJSDate())
		}
	}

	setDisplayDate (dateTime: DateTime) {
		this.displayDate = dateTime

		this.year = dateTime.year.toString()
		this.month = dateTime.month.toString()
		this.day = dateTime.day.toString()
		this.hour = (dateTime.hour > 12 ? dateTime.hour - 12 : dateTime.hour).toString()
		if (this.hour === "0") this.hour = "12"
		this.minute = dateTime.minute.toString()
		this.ampm = dateTime.hour >= 12 ? "pm" : "am"
	}

	getValuePart (datePart: string) {
		return get(this, datePart)
	}
	setValuePart (datePart: string, value: string) {
		return set(this, datePart, value)
	}

	clearDate () {
		this.setValue(null)
		this.$emit("input", null)
	}

	openCalender (event: any, which: string) {
		(this.$refs as any)[which].open(event)
	}

	dateAsString (date: Date) {
		return date.getUTCFullYear().toString() +
			"/" + date.getUTCMonth() +
			"/" + date.getDate()
	}

	async setDate (newDate: Date, normalizeTime: boolean = false) {
		this.setValue(newDate, normalizeTime)
		await Vue.nextTick()

		this.updateDate()
	}

	yesterday (): Date {
		let date = this.today()
		date.setDate(date.getDate() - 1)
		return date
	}

	tomorrow (): Date {
		let date = this.today()
		date.setDate(date.getDate() + 1)
		return date
	}

	today () {
		const now = DateTime.now()
		const dateTime = this.includeTime ? now : now.startOf("day")

		if (this.enterAsUTC) {
			// DateTime's typescript definition is out of date. We have to convert to any to be able to use the second param.
			const utcDateTime = (DateTime as any).fromObject({
				year: dateTime.year,
				month: dateTime.month,
				day: dateTime.day,
				hour: this.includeTime ? dateTime.hour : 0,
				minute: this.includeTime ? dateTime.minute : 0,
				second: this.includeTime ? dateTime.second : 0
			}, { zone: "utc" })

			return utcDateTime.toJSDate()
		}

		return dateTime.toJSDate()
	}

	setToYesterday () {
		this.setDate(this.yesterday(), true)
	}

	setToToday () {
		this.setDate(this.today(), true)
	}

	setToTomorrow () {
		this.setDate(this.tomorrow(), true)
	}

	clearTime (date: Date) {
		// Set the date's time value equal to the timezone offset (i.e., 00:00:00 GMT+1400 = 14:00:00 GMT+1400)
		date.setHours(0)
		date.setMinutes(0)
		date.setSeconds(0)

		let timezoneOffsetHours = date.getTimezoneOffset() / 60
		date.setHours(timezoneOffsetHours * -1)
	}

	isDateInValidRange (date: Date | null) {
		if (!date) {
			return false
		}

		if (this.minDateTime && date < this.minDateTime) {
			return false
		}

		if (this.maxDateTime && date > this.maxDateTime) {
			return false
		}

		return true
	}

	get showYesterdayButton (): boolean {
		return this.includeYesterday && this.isDateInValidRange(this.yesterday())
	}

	get showTodayButton (): boolean {
		return this.includeToday && this.isDateInValidRange(this.today())
	}

	get showTomorrowButton (): boolean {
		return this.includeTomorrow && this.isDateInValidRange(this.tomorrow())
	}

	get minDateTimeParsed (): string | null {
		if (!this.minDateTime) {
			return null
		}

		return typeof this.minDateTime === "string" ? this.minDateTime : this.minDateTime.toISOString()
	}

	get maxDateTimeParsed (): string | null {
		if (!this.maxDateTime) {
			return null
		}

		return typeof this.maxDateTime === "string" ? this.maxDateTime : this.maxDateTime.toISOString()
	}

	get selectAttribute () {
		return {
			highlight: {
				contentClass: "vc-selected-day",
				fillMode: "solid",
				color: "blue"
			}
		}
	}

	/**
	 * Processes the date selected from the calendar picker.
	 *
	 * v-date-picker behavior varies by context:
	 * - Initial selection includes the current time.
	 * - Subsequent selections default to midnight (00:00:00).
	 *
	 * Ensures consistent time by:
	 * - Applies time components based on component's current state:
	 *   - For includeTime=true: uses current hour/minute values or falls back to system time
	 *   - For endOfDay=true: sets time to 23:59:59
	 *   - Otherwise: sets time to 00:00:00
	 * - Handles AM/PM conversion.
	 * - Applies appropriate timezone handling (UTC or local) based on prop settings
	 *
	 * @param selectedValue - The Date object emitted by v-date-picker
	 */
	dateSelected (selectedValue: Date) {
		if (!this.noPopup && this.$refs.datePicker?.hidePopover) {
			this.$refs.datePicker.hidePopover()
		}

		if (!this.pickingDate) {
			return
		}

		this.pickingDate = false

		const dateObject: any = {
			year: selectedValue.getFullYear(),
			month: selectedValue.getMonth() + 1,
			day: selectedValue.getDate()
		}

		if (this.includeTime) {
			// For time fields, use current time or maintain existing time
			const now = DateTime.now()
			dateObject.hour = parseInt(this.hour) || now.hour

			if (this.ampm === "pm" && dateObject.hour < 12) {
				dateObject.hour += 12
			} else if (this.ampm === "am" && dateObject.hour === 12) {
				dateObject.hour = 0
			}

			dateObject.minute = parseInt(this.minute) || now.minute
			dateObject.second = 0
		} else if (this.endOfDay) {
			// Set to end of day
			dateObject.hour = 23
			dateObject.minute = 59
			dateObject.second = 59
		} else {
			// Set to start of day
			dateObject.hour = 0
			dateObject.minute = 0
			dateObject.second = 0
		}

		const readOptions = this.enterAsUTC
			? { zone: "utc" }
			: {}

		const adjustedDateTime = (DateTime as any).fromObject(dateObject, readOptions) as DateTime

		this.setValue(adjustedDateTime.toJSDate(), true)
	}

	quickOptionDirectionKeyPressed (active: string, direction: string) {
		if (active === "date") {
			if (direction === "right") {
				if (this.showYesterdayButton) this.$refs.yesterdayButton.focus()
				else if (this.showTodayButton) this.$refs.todayButton.focus()
				else if (this.showTomorrowButton) this.$refs.tomorrowButton.focus()
			}
		} else if (active === "yesterday") {
			if (direction === "left") {
				this.$refs.datePickerButton.focus()
			} else if (direction === "right") {
				if (this.showTodayButton) this.$refs.todayButton.focus()
				else if (this.showTomorrowButton) this.$refs.tomorrowButton.focus()
			}
		} else if (active === "today") {
			if (direction === "left") {
				if (this.showYesterdayButton) this.$refs.yesterdayButton.focus()
				else this.$refs.datePickerButton.focus()
			} else if (direction === "right") {
				this.$refs.tomorrowButton.focus()
			}
		} else if (active === "tomorrow") {
			if (direction === "left") {
				if (this.showTodayButton) this.$refs.todayButton.focus()
				else if (this.showYesterdayButton) this.$refs.yesterdayButton.focus()
				else this.$refs.datePickerButton.focus()
			} else if (direction === "right") {
				if (this.showTomorrowButton) this.$refs.tomorrowButton.focus()
			}
		}
	}

	@Watch("pickingDate")
	async datePickerVisibilityChanged (pickerVisible: boolean) {
		if (pickerVisible) {
			await this.$nextTick()
			const vDatePickerEl = this.$refs.datePicker.$el.querySelector(".vc-weeks")
			const todayDateOption = // selected day or first tabbable date
				vDatePickerEl?.querySelector(".vc-selected-day") as HTMLElement ??
				vDatePickerEl?.querySelector("[tabindex='0']") as HTMLElement

			if (todayDateOption) todayDateOption.focus()
		} else {
			this.$refs.datePickerButton.focus()
		}
	}
}

